/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/managerProfileApi.ts
 * PURPOSE: API client màn Thông tin của tôi — Release v0 WP-5 / SC-013-MGR-PROFILE
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-17
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 *
 * @business-context
 * - Domain: Manager — Hồ sơ cá nhân + tự chấm công (Trưởng ca / Quản lý chi nhánh)
 * - Impact: UX Critical — khối [2] Hồ sơ, [4] Chấm công của tôi, [M] Đổi mật khẩu
 * - Stakeholders: Branch Managers, Shift Managers, Head Chefs
 *
 * @technical-architecture
 * - Layer: Data Access Layer (API Services)
 * - Pattern: Axios HTTP client (apiV2 cho /api/v2, api cho /api/v1), Zero Mock
 * - Dependencies: axios (via api/apiV2 from config), TypeScript 5.0+
 *
 * @cross-references
 * - Mockup: docs/02-design/04-ui-ux-design/design-imports/ui-ux-nqhbot/v0/schedule/manager/profile.html
 * - Biến thể BM: .../manager/profile-bm.html
 * - Backend: backend/app/api/v2/endpoints/attendance.py (status/history/clock-config/check-in/out)
 * - Backend: backend/app/api/v2/endpoints/employee.py (GET /profile)
 * - Backend: backend/app/api/v1/tenant_users.py:1158 (POST /change-password)
 * - Hooks: frontend/src/hooks/useManagerProfile.ts
 * - 2026-08-17: SelfAttendanceRecord gained status_labels/resolution_source/
 *   manager_resolved_by_name/manager_resolved_at/has_complaint — employee-facing
 *   "Lịch sử xử lý" (read-only equivalent of manager-side [18-LOG] popup, deferred
 *   there, built here). Consumer: frontend/src/pages/AttendanceHistory.tsx
 * ===============================================================================
 */

import { api, apiV2 } from "./config";
import { useAuthStore } from "@/store/auth";

// =============================================================================
// Types — mirror backend Pydantic schemas (WP-5)
// =============================================================================

/** GET /api/v2/employee/profile (các field khối [2] dùng — schema đầy đủ hơn) */
export interface ManagerSelfProfile {
  id: string;
  employee_code: string;
  full_name: string;
  phone_number: string | null;
  email: string | null;
  position: string | null;
  branch_name: string | null;
  branch_code: string | null;
  department_name: string | null;
  organization_name: string | null;
  username: string | null;
}

/** Ca hôm nay — khối [4] "Ca hôm nay: M6 · Ca sáng 07:00–17:00" (null → ẩn dòng ca) */
export interface TodayShiftInfo {
  schedule_id: string;
  shift_code: string | null;
  shift_name: string | null;
  shift_type: string | null;
  start_time: string; // "HH:MM"
  end_time: string; // "HH:MM"
}

/** GET /api/v2/attendance/status */
export interface SelfAttendanceStatus {
  status: "NOT_CLOCKED_IN" | "CLOCKED_IN" | "CLOCKED_OUT";
  last_clock_in: string | null;
  last_clock_out: string | null;
  today_hours: number;
  is_late: boolean;
  is_early_leave: boolean;
  current_attendance_id: string | null;
  employee_id: string | null;
  branch_id: string | null;
  today_shift: TodayShiftInfo | null;
  is_approved: boolean;
  clock_in_within_geofence: boolean | null;
  clock_in_gps_distance_m: number | null;
  branch_latitude: number | null;
  branch_longitude: number | null;
}

/** GET /api/v2/attendance/clock-config — popup [P] chỉ hiện phương thức được bật */
export interface ClockConfig {
  selfie_enabled: boolean;
  gps_enabled: boolean;
  gps_radius_meters: number;
  face_id_enabled: boolean;
  gps_photo_enabled: boolean;
  timemark_method_enabled: boolean;
}

/** Bản ghi trong GET /api/v2/attendance/history (field khối [6] dùng) */
export interface SelfAttendanceRecord {
  id: string;
  schedule_id: string | null;
  clock_in: string;
  clock_out: string | null;
  status: string;
  is_late: boolean;
  late_minutes: number;
  is_early_leave: boolean;
  early_leave_minutes: number;
  is_approved: boolean;
  shift_code: string | null;
  shift_name: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  clock_in_within_geofence: boolean | null;
  clock_in_gps_distance_m: number | null;
  /**
   * 2026-08-17 — "Lịch sử xử lý" (SC-011-ATTEND-MGR resolution-log gap, employee-
   * facing read-only equivalent of the manager-side [18-LOG] popup). Backend:
   * backend/app/api/v2/endpoints/attendance.py get_attendance_history() +
   * backend/app/schemas/v2/attendance.py AttendanceResponseV2. `resolution_source`
   * is null when the record hasn't been through auto-match/manager-resolve yet
   * (e.g. still clocked-in) — that state must render as "chưa xử lý", not fall
   * through to either the "auto" or "manager" branch.
   */
  /**
   * 2026-09-04 — backend ĐÃ trả 2 field này trong mọi bản ghi history
   * (backend/app/schemas/v2/attendance.py AttendanceResponseV2 dòng 241/243,
   * kiểm chứng bằng curl thật: `"total_hours": 0.14, "overtime_hours": 0.0`),
   * FE chỉ chưa khai nên màn Lịch sử không hiện số giờ làm — trong khi mobile
   * hiện (`record.total_hours` ở AttendanceScreen.tsx renderHistory). Cùng họ
   * "thiếu khai báo contract" với `must_change_password` (types/auth.ts).
   */
  total_hours: number;
  overtime_hours: number;
  status_labels: string[];
  status_labels_vi: string[];
  resolution_source: "auto" | "manager" | null;
  manager_resolved_by: string | null;
  manager_resolved_by_name: string | null;
  manager_resolved_at: string | null;
  has_complaint: boolean;
}

export interface SelfAttendanceHistory {
  records: SelfAttendanceRecord[];
  total: number;
}

/** Lịch ca của tôi — GET /api/v2/schedule/my-schedule (nhận diện ngày "nghỉ theo lịch") */
export interface MyScheduleItem {
  id: string;
  date: string; // "yyyy-mm-dd"
  start_time: string;
  end_time: string;
  shift_type: string | null;
  status: string;
}

export interface MyScheduleResponse {
  today: MyScheduleItem | null;
  this_week: MyScheduleItem[];
  upcoming: MyScheduleItem[];
}

/** POST /api/v2/attendance/check-in payload (GPS bắt buộc) */
export interface SelfClockPayload {
  latitude: number;
  longitude: number;
  accuracy?: number;
  /**
   * v0 chưa tích hợp camera capture (xem NOTE trong ManagerProfilePage modal [C1])
   * → KHÔNG gửi photo_url, KHÔNG mock ảnh. Backend nhận photo_url optional.
   */
  photo_url?: string;
  notes?: string;
  device_info?: Record<string, unknown>;
}

export interface ChangePasswordPayload {
  old_password: string;
  new_password: string;
}

/**
 * BS-301 Route 2 §1 (2026-08-17, emp-06 "lịch sử+OT") — GET
 * /api/v2/attendance/ot-summary?month=YYYY-MM (backend/app/api/v2/endpoints/
 * attendance_sprint71.py:412-475, OTSummaryResponse). Self-scoped by default
 * (employee sees own data — no employee_id param sent here).
 */
export interface SelfOtSummary {
  employee_id: string;
  year_month: string;
  total_ot_hours: number;
  weekday_ot_hours: number;
  weekend_ot_hours: number;
  holiday_ot_hours: number;
  night_ot_hours: number;
  monthly_cap: number;
  remaining_cap: number;
  is_warning: boolean;
  is_exceeded: boolean;
  late_count: number;
  late_total_minutes: number;
  early_count: number;
  rest_violations: number;
  completed_count: number;
}

// =============================================================================
// API calls
// =============================================================================

export const managerProfileApi = {
  /** Khối [2] Hồ sơ — GET /api/v2/employee/profile */
  getProfile: async (): Promise<ManagerSelfProfile> => {
    const response = await apiV2.get<ManagerSelfProfile>("/employee/profile");
    return response.data;
  },

  /** Khối [4] trạng thái hôm nay — GET /api/v2/attendance/status */
  getAttendanceStatus: async (): Promise<SelfAttendanceStatus> => {
    const response = await apiV2.get<SelfAttendanceStatus>("/attendance/status");
    return response.data;
  },

  /** Popup [P] phương thức được bật — GET /api/v2/attendance/clock-config */
  getClockConfig: async (): Promise<ClockConfig> => {
    const response = await apiV2.get<ClockConfig>("/attendance/clock-config");
    return response.data;
  },

  /** Khối [6] Gần đây — GET /api/v2/attendance/history (bản ghi của chính mình) */
  getHistory: async (startDate: string, endDate: string): Promise<SelfAttendanceHistory> => {
    const response = await apiV2.get<SelfAttendanceHistory>("/attendance/history", {
      params: { start_date: startDate, end_date: endDate, limit: 50 },
    });
    return response.data;
  },

  /** Nhận diện ngày nghỉ theo lịch — GET /api/v2/schedule/my-schedule */
  getMySchedule: async (): Promise<MyScheduleResponse> => {
    const response = await apiV2.get<MyScheduleResponse>("/schedule/my-schedule");
    return response.data;
  },

  /** Xác nhận VÀO — POST /api/v2/attendance/check-in */
  checkIn: async (payload: SelfClockPayload): Promise<void> => {
    await apiV2.post("/attendance/check-in", payload);
  },

  /** Xác nhận RA — POST /api/v2/attendance/check-out */
  checkOut: async (payload: SelfClockPayload): Promise<void> => {
    await apiV2.post("/attendance/check-out", payload);
  },

  /** Modal [M] — POST /api/v1/tenant-users/change-password (self, cần MK hiện tại) */
  changePassword: async (payload: ChangePasswordPayload): Promise<void> => {
    const { data } = await api.post<{ access_token: string; refresh_token: string }>(
      "/tenant-users/change-password",
      payload,
    );
    // #1007: the bump revokes this device's token too; adopt the fresh pair the
    // backend minted at the new token_version so the session continues.
    useAuthStore.getState().setTokens(data.access_token, data.refresh_token);
  },

  /**
   * BS-301 Route 2 §1 (emp-06) — GET /api/v2/attendance/ot-summary?month=YYYY-MM,
   * self-scoped (no employee_id → the calling employee's own OT breakdown).
   */
  getOtSummary: async (month: string): Promise<SelfOtSummary> => {
    const response = await apiV2.get<SelfOtSummary>("/attendance/ot-summary", {
      params: { month },
    });
    return response.data;
  },

  /**
   * 2026-09-04 — CÙNG endpoint, chế độ KHOẢNG NGÀY: backend đã có sẵn nhánh
   * `start_date`+`end_date` → `OTCalculationService.get_range_summary()`
   * (attendance_sprint71.py get_ot_summary), kiểm chứng bằng curl thật:
   * `year_month` trả về "2026-08-01..2026-09-05". Không thêm hợp đồng API mới.
   *
   * Vì sao màn Lịch sử dùng nhánh KHOẢNG thay vì `month`:
   *   (a) thống kê phải nói về ĐÚNG khoảng người dùng đang xem — trước đây thẻ
   *       tháng nằm trên một danh sách 7 ngày, hai khoảng khác nhau không nhãn;
   *   (b) nhánh `month` tính `completed_count` = 0 CỨNG (_compute_monthly_summary
   *       không truyền field này, khác _compute_range_summary) — kiểm chứng:
   *       cùng nhân viên, `month=2026-08` trả completed_count 0 trong khi
   *       khoảng 01/08–05/09 trả 1.
   */
  getOtSummaryRange: async (startDate: string, endDate: string): Promise<SelfOtSummary> => {
    const response = await apiV2.get<SelfOtSummary>("/attendance/ot-summary", {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },
};
